import './index.less';
import { isNumber, pull, uniq } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    BaseViewportPlugin,
    checkPointInViewport,
    convertDocumentPointToNode,
    getFramework,
    getLayerId,
    getMouseMoveRect,
    getOperateType,
    getPointRect,
    getStageIndexByMousePointY,
    MoveListenerLevel,
    useIdentity,
    useIsLongImageMode,
    useMoveEventListener,
} from '@/framework';
import { type GroupLayer } from '@/layers/group-layer';
import { type Layer } from '@/layers/layer';
import { getCollisionIds, IShape } from '@/utils/collision-kit';
import { isHotKeyPress, isLeftButton } from '@/utils/event-kit';
import { BaseLayerViewType, IExportPlugin, PluginType } from '@/version-types';
import { getGroupPickState } from '../states/group-inner-state';

function tryGetGroupChildIds(identity: string, brokeExtraGroup = true, autoRerender = false) {
    const framework = getFramework(identity);
    const activeLayer = framework.operate.getActiveLayer();
    if (!activeLayer) return;
    const ids: string[] = [];
    if (framework.checkIsExtraGroup(activeLayer)) {
        (activeLayer.children || []).forEach((item) => {
            ids.push(item.id);
        });
        if (brokeExtraGroup) {
            const res = framework.tryRemoveGroup(activeLayer, autoRerender);
            if (res && autoRerender) {
                framework.operate.delActiveLayer();
            }
        }
    } else {
        ids.push(activeLayer.id);
    }
    return ids;
}

const PickPlugin = React.memo(() => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const wrapRef = useRef<HTMLDivElement>(null);
    const isLongImageMode = useIsLongImageMode();

    const [moveRect, setMoveRect] = useState<IShape>({
        left: 0,
        top: 0,
        width: 0,
        height: 0,
    });

    const collisionsRef = useRef<IShape[]>([]);
    const groupPickIdsRef = useRef<string[]>([]);
    const renderRectRef = useRef(false);
    const [isSelect, setIsSelect] = useState(false);
    const pickGroupInnerIdRef = useRef('');
    const getGroupInnerFns = getGroupPickState(identity);
    const innerIds = useRef<string[]>([]);

    const tryGetGroupLayer = useCallback(() => {
        const groupLayer = framework.getInstance<GroupLayer>('GroupLayer');
        if (!groupLayer) {
            throw new Error('GroupLayer is not found');
        }
        return groupLayer;
    }, []);

    const tryGetLayer = useCallback(() => {
        const layer = framework.getInstance<Layer>('Layer');
        if (!layer) {
            throw new Error('Layer is not found');
        }
        return layer;
    }, []);

    // 实现鼠标点击
    useEffect(() => {
        const mouseDownPoint = { x: 0, y: 0 };

        const downHandle = framework.events.onMouseDown((e) => {
            if (!isLeftButton(e)) return;
            mouseDownPoint.x = e.pageX;
            mouseDownPoint.y = e.pageY;
            const hasHotKey = isHotKeyPress();
            const activeLayer = framework.operate.getActiveLayer();
            let stopPropagation = hasHotKey;
            let finalActiveLayerId = '';

            if (!hasHotKey && activeLayer && !framework.operate.getHoverId()) {
                const mouseDownRect = getPointRect(identity, {
                    x: e.pageX,
                    y: e.pageY,
                });

                const layerId = framework.stage.tryPickWrap(
                    mouseDownRect,
                    framework.getCollisionLayers(framework.stage.getActiveOriginIndex(), true, true),
                );

                if (layerId && layerId !== activeLayer.id) {
                    const layer = framework.getLayer(layerId);
                    if (framework.checkIsGroup(layer)) {
                        const activeInfo = framework.operate.getActiveInfo();
                        const pickChildId = framework.stage.tryPickChild(mouseDownRect, layer.children);
                        if (pickChildId && pickChildId !== activeInfo?.info.pickId) {
                            pickGroupInnerIdRef.current = layerId;
                            console.log('layerId 10', layerId);
                            stopPropagation = true;
                        }
                    } else {
                        console.log('layerId 11', layerId);
                        pickGroupInnerIdRef.current = layerId;
                        stopPropagation = true;
                    }
                }

                if (!stopPropagation && framework.checkIsFormalGroup(activeLayer)) {
                    const activeInfo = framework.operate.getActiveInfo();
                    const pickOtherChildId = framework.stage.tryPickChild(mouseDownRect, activeLayer.children);
                    if (pickOtherChildId && pickOtherChildId !== activeInfo?.info.pickId) {
                        pickGroupInnerIdRef.current = pickOtherChildId;
                        console.log('layerId 12', layerId);
                        stopPropagation = true;
                    }
                    if (!stopPropagation) {
                        let isPickCurrent = false;
                        if (activeInfo?.info.pickId) {
                            isPickCurrent = !!framework.stage.tryPickChild(mouseDownRect, [
                                framework.getLayer(activeInfo.info.pickId),
                            ]);
                            if (isPickCurrent) {
                                finalActiveLayerId = activeInfo.info.pickId;
                            }
                        }
                        const operateType = getOperateType(e.target);
                        const isMoveOperate = operateType === BaseLayerViewType.MOVE || !operateType;

                        if (!isPickCurrent && isMoveOperate) {
                            const groupInnerId = framework.stage.tryPickChild(mouseDownRect, activeLayer.children);
                            if (groupInnerId) {
                                stopPropagation = true;
                                console.log('layerId 13', layerId);
                                pickGroupInnerIdRef.current = groupInnerId;
                            } else {
                                const originIndex = framework.tryGetLayerOriginIndex(activeLayer.id);
                                if (isNumber(originIndex)) {
                                    const groupOuterId = framework.stage.tryPickWithoutId(
                                        mouseDownRect,
                                        framework.getCollisionLayers(originIndex, true, true),
                                        activeLayer.id,
                                    );
                                    if (groupOuterId) {
                                        stopPropagation = true;
                                        finalActiveLayerId = groupOuterId;
                                        framework.operate.setActiveLayer(groupOuterId);
                                    }
                                }
                            }
                        }
                        if (isPickCurrent && activeLayer.lock) {
                            stopPropagation = true;
                        }
                    }
                }
            }

            framework.operate.hoverExtend.apply(finalActiveLayerId);

            return stopPropagation;
        }, 1);

        const upHandle = framework.events.onMouseUp((e) => {
            if (!isLeftButton(e)) return;
            const hasHotKey = isHotKeyPress();
            const mouseUpPoint = { x: e.pageX, y: e.pageY };
            const isMove = mouseUpPoint.x !== mouseDownPoint.x || mouseUpPoint.y !== mouseDownPoint.y;
            let stopPropagation = hasHotKey || isMove;
            const activeLayer = framework.operate.getActiveLayer();
            const downGroupInnerId = pickGroupInnerIdRef.current;
            pickGroupInnerIdRef.current = '';
            let finalActiveLayerId = '';

            if (!isMove) {
                if (downGroupInnerId && !tryGetLayer().checkWrapLayerProps(downGroupInnerId, 'hidden', true)) {
                    finalActiveLayerId = downGroupInnerId;
                    console.log('layerId 4', downGroupInnerId);
                    framework.operate.setActiveLayer(downGroupInnerId);
                } else {
                    const mouseDownRect = getPointRect(identity, {
                        x: e.pageX,
                        y: e.pageY,
                    });
                    if (hasHotKey) {
                        let targetLayerId = getLayerId(identity, e.target);
                        // 如果开启格式刷则停止临时组操作
                        if (framework.operate.hoverExtend.get()) {
                            if (targetLayerId && targetLayerId !== activeLayer?.id) {
                                const layer = framework.getLayer(targetLayerId);
                                // 目标为正式组
                                if (layer.children?.length) {
                                    finalActiveLayerId =
                                        framework.stage.tryPickChild(mouseDownRect, layer.children) || targetLayerId;
                                    stopPropagation = true;
                                } else {
                                    finalActiveLayerId = targetLayerId;
                                    stopPropagation = true;
                                }
                            }
                        } else {
                            if (!targetLayerId || targetLayerId !== activeLayer?.id) {
                                if (!targetLayerId) {
                                    if (activeLayer) {
                                        if (framework.checkIsExtraGroup(activeLayer)) {
                                            const groupInnerId = framework.stage.tryPickWrap(
                                                mouseDownRect,
                                                activeLayer.children,
                                            );
                                            // 临时组内，取消选中
                                            if (groupInnerId) {
                                                const childIds = tryGetGroupChildIds(identity, true, true);
                                                if (childIds) {
                                                    pull(childIds, groupInnerId);
                                                    if (childIds.length) {
                                                        stopPropagation = true;
                                                        if (childIds.length === 1) {
                                                            finalActiveLayerId = childIds[0];
                                                            console.log('layerId 5');
                                                            framework.operate.setActiveLayer(finalActiveLayerId);
                                                        } else {
                                                            tryGetGroupLayer().createGroupAndActive(childIds);
                                                        }
                                                    }
                                                }
                                            } else {
                                                // 临时组外，选中
                                                targetLayerId = framework.stage.tryPickWithoutId(
                                                    mouseDownRect,
                                                    framework.getCollisionLayers(
                                                        framework.stage.getActiveOriginIndex(),
                                                        true,
                                                        true,
                                                    ),
                                                    activeLayer.id,
                                                );
                                            }
                                        } else if (framework.checkIsFormalGroup(activeLayer)) {
                                            const groupInnerId = framework.stage.tryPickChild(
                                                mouseDownRect,
                                                activeLayer.children,
                                            );
                                            if (!groupInnerId) {
                                                const originIndex = framework.tryGetLayerOriginIndex(activeLayer.id);
                                                if (isNumber(originIndex)) {
                                                    targetLayerId = framework.stage.tryPickWithoutId(
                                                        mouseDownRect,
                                                        framework.getCollisionLayers(originIndex, true, true),
                                                        activeLayer.id,
                                                    );
                                                }
                                            }
                                        }
                                    }
                                }
                            } else {
                                if (activeLayer) {
                                    const originIndex = framework.tryGetLayerOriginIndex(activeLayer.id);
                                    if (isNumber(originIndex)) {
                                        targetLayerId = framework.stage.tryPickWithoutId(
                                            mouseDownRect,
                                            framework.getCollisionLayers(originIndex, true, true),
                                            activeLayer.id,
                                        );
                                    }
                                }
                            }
                            if (targetLayerId) {
                                const childIds = tryGetGroupChildIds(identity, true, false);
                                stopPropagation = true;
                                if (childIds) {
                                    childIds.push(targetLayerId);
                                    // 如果存在锁定图层，则选中
                                    let hasLock = false;
                                    for (let i = 0; i < childIds.length; i++) {
                                        const id = childIds[i];
                                        const layer = framework.getLayer(id);
                                        if (layer.lock) {
                                            hasLock = true;
                                            break;
                                        }
                                    }
                                    if (hasLock) {
                                        console.log('layerId 6');
                                        framework.operate.setActiveLayer(targetLayerId);
                                    } else {
                                        tryGetGroupLayer().createGroupAndActive(childIds);
                                    }
                                } else {
                                    finalActiveLayerId = targetLayerId;
                                    console.log('layerId 7');
                                    framework.operate.setActiveLayer(targetLayerId);
                                }
                            }
                        }
                    } else {
                        if (activeLayer && framework.checkIsExtraGroup(activeLayer)) {
                            const mouseDownRect = getPointRect(identity, {
                                x: e.pageX,
                                y: e.pageY,
                            });
                            const groupInnerId = framework.stage.tryPickWrap(mouseDownRect, activeLayer.children);
                            if (groupInnerId) {
                                stopPropagation = true;
                                finalActiveLayerId = groupInnerId;
                                console.log('layerId 8');
                                framework.operate.setActiveLayer(groupInnerId);
                            } else {
                                const originIndex = framework.tryGetLayerOriginIndex(activeLayer.id);
                                if (isNumber(originIndex)) {
                                    const groupOuterId = framework.stage.tryPickWithoutId(
                                        mouseDownRect,
                                        framework.getCollisionLayers(originIndex, true, true),
                                        activeLayer.id,
                                    );
                                    if (groupOuterId) {
                                        stopPropagation = true;
                                        finalActiveLayerId = groupOuterId;
                                        console.log('layerId 9');
                                        framework.operate.setActiveLayer(groupOuterId);
                                    }
                                }
                            }
                        }
                    }
                }
            }
            framework.operate.hoverExtend.apply(finalActiveLayerId);

            return stopPropagation;
        }, 1);
        return () => {
            framework.events.off(downHandle);
            framework.events.off(upHandle);
        };
    }, []);

    // 实现鼠标框选
    useMoveEventListener(
        (e) => {
            pickGroupInnerIdRef.current = '';
            renderRectRef.current = false;
            const isDisableOperate = framework.operate.isDisableOperate();
            const isDisablePick = framework.operate.isDisablePick();
            const isDisableMouseSelect = framework.operate.isDisableMouseSelect();
            if (isDisableOperate || isDisablePick || isDisableMouseSelect) return;

            const hoverExtend = framework.operate.hoverExtend.get();
            if (hoverExtend) return;

            const isInViewport = checkPointInViewport(identity, e.startPoint);
            if (!isInViewport) return;

            const operateType = getOperateType(e.target);
            if (operateType) {
                return;
            }

            const activeLayer = framework.operate.getActiveLayer();
            if (getGroupInnerFns.getIsActive() && activeLayer?.children) {
                // 组内框选逻辑
                if (e.isStart) {
                    setIsSelect(true);
                    innerIds.current = getGroupInnerFns.getPickIds();
                    collisionsRef.current = framework.stage.convertToShapes(activeLayer.children, true);
                }
                renderRectRef.current = !e.isEnd;

                const { startPoint, currentPoint } = e;

                // 获取鼠标选择区域，进行绘制鼠标选择框
                const moveRect = getMouseMoveRect(
                    convertDocumentPointToNode(identity, startPoint, wrapRef.current),
                    convertDocumentPointToNode(identity, currentPoint, wrapRef.current),
                );
                setMoveRect(moveRect);
                framework.operate.setIsMouseSelect(true);

                // 获取碰撞的图层id，进行绘制图层边框
                const moveCollisionRect = getMouseMoveRect(
                    convertDocumentPointToNode(identity, startPoint),
                    convertDocumentPointToNode(identity, currentPoint),
                );

                const collisionsIds = getCollisionIds(moveCollisionRect, collisionsRef.current);
                const ids = uniq(collisionsIds.concat(innerIds.current));

                getGroupInnerFns.setPickIds(ids, true);

                if (e.isEnd) {
                    innerIds.current = [];
                    setIsSelect(false);
                    framework.operate.setIsMouseSelect(false);
                }

                return;
            }

            if (activeLayer?.lock) {
                framework.operate.delActiveLayer();
            } else {
                const layerId = getLayerId(identity, e.target);
                if (layerId && !framework.getLayer(layerId).lock) {
                    return;
                }
            }

            if (e.isStart) {
                setIsSelect(true);
            }

            if (!wrapRef.current) return;

            const hasHotKey = isHotKeyPress();
            if (e.isStart && !hasHotKey && isLongImageMode) {
                const stageIndex = getStageIndexByMousePointY(identity, e.startPoint.y);
                const activeStageIndex = framework.stage.getActiveStageIndex();
                if (stageIndex !== activeStageIndex) {
                    framework.stage.setActiveStageIndex(stageIndex);
                }
            }
            const { startPoint, currentPoint } = e;

            if (e.isStart && hasHotKey) {
                const childIds = tryGetGroupChildIds(identity, true, true);
                if (childIds) {
                    groupPickIdsRef.current = childIds;
                    framework.operate.delActiveLayer();
                }
            }

            if (!hasHotKey) {
                groupPickIdsRef.current = [];
            }

            if (e.isStart) {
                collisionsRef.current = framework.stage.getShapes(framework.stage.getActiveStageIndex(), false);
            }

            renderRectRef.current = !e.isEnd;

            // 获取鼠标选择区域，进行绘制鼠标选择框
            const moveRect = getMouseMoveRect(
                convertDocumentPointToNode(identity, startPoint, wrapRef.current),
                convertDocumentPointToNode(identity, currentPoint, wrapRef.current),
            );
            setMoveRect(moveRect);
            framework.operate.setIsMouseSelect(true);

            // 获取碰撞的图层id，进行绘制图层边框
            const moveCollisionRect = getMouseMoveRect(
                convertDocumentPointToNode(identity, startPoint),
                convertDocumentPointToNode(identity, currentPoint),
            );
            const collisionsIds = getCollisionIds(moveCollisionRect, collisionsRef.current);
            const ids = uniq(collisionsIds.concat(groupPickIdsRef.current));
            framework.operate.setSelected(ids);

            if (e.isEnd && ids.length) {
                if (ids.length === 1) {
                    framework.operate.setActiveLayer(ids[0]);
                } else {
                    tryGetGroupLayer().createGroupAndActive(ids);
                }
            }

            if (e.isEnd) {
                groupPickIdsRef.current = [];
                framework.operate.clearSelected();
                setIsSelect(false);
                framework.operate.setIsMouseSelect(false);
            }
        },
        MoveListenerLevel.DEFAULT_LEVEL,
        'pick-plugin',
    );

    if (!isSelect) return null;

    return (
        <BaseViewportPlugin ref={wrapRef} name="pick-layer">
            {(!moveRect.width && !moveRect.height) || !renderRectRef.current ? null : (
                <div
                    className="mouse-select-rect"
                    style={{
                        left: `${moveRect.left}px`,
                        top: `${moveRect.top}px`,
                        width: `${moveRect.width}px`,
                        height: `${moveRect.height}px`,
                    }}
                ></div>
            )}
        </BaseViewportPlugin>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.VIEWPORT,
    sort: 99,
    name: 'pick-layer',
    render() {
        return <PickPlugin />;
    },
};

export default exportPlugin;
